import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Sparkles, Send, Save, X, Trash2, FileText,
  Users, RefreshCw, Wand2, Check, AlertCircle, Clock, Calendar,
} from "lucide-react";

const TONES = [
  { key: "professional", label: "Professional" },
  { key: "friendly",     label: "Friendly" },
  { key: "appreciative", label: "Appreciative" },
  { key: "urgent",       label: "Urgent" },
  { key: "stern",        label: "Stern" },
];

export default function AiCommandPanel({ API, getAuthHeaders, currentUser }) {
  const [staff, setStaff] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [intent, setIntent] = useState("");
  const [tone, setTone] = useState("professional");
  const [context, setContext] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [scheduledFor, setScheduledFor] = useState(""); // local datetime string YYYY-MM-DDTHH:mm
  const [draftId, setDraftId] = useState(null); // if loaded from saved drafts
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sentSummary, setSentSummary] = useState(null);

  const fetchStaff = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/ai-command/staff`, { headers: getAuthHeaders() });
      setStaff(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load staff");
    }
  }, [API, getAuthHeaders]);

  const fetchDrafts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/ai-command/drafts`, { headers: getAuthHeaders() });
      setDrafts(data || []);
    } catch {
      // non-fatal
    }
  }, [API, getAuthHeaders]);

  useEffect(() => { fetchStaff(); fetchDrafts(); }, [fetchStaff, fetchDrafts]);

  const toggleRecipient = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const resetAll = () => {
    setSelectedIds([]); setIntent(""); setTone("professional"); setContext("");
    setSubject(""); setBody(""); setScheduledFor(""); setDraftId(null); setSentSummary(null);
  };

  const generate = async () => {
    if (selectedIds.length === 0) return toast.error("Pick at least one recipient");
    if (!intent.trim()) return toast.error("Write what you want to convey");
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/admin/ai-command/draft`,
        { recipient_ids: selectedIds, intent, tone, context },
        { headers: getAuthHeaders() }
      );
      setSubject(data.subject || "");
      setBody(data.body || "");
      toast.success("Draft generated. Edit then send or save.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI failed to draft email");
    }
    setGenerating(false);
  };

  const saveDraft = async () => {
    if (!subject.trim() || !body.trim()) return toast.error("Subject and body required");
    if (scheduledFor && selectedIds.length === 0) return toast.error("Pick at least one recipient for a scheduled send");
    setSaving(true);
    try {
      // scheduledFor is local datetime; send ISO UTC if set
      const scheduled_iso = scheduledFor ? new Date(scheduledFor).toISOString() : null;
      const payload = {
        recipient_ids: selectedIds,
        recipient_names: staff.filter(s => selectedIds.includes(s.id)).map(s => s.name),
        subject, body, tone, intent,
        scheduled_for: scheduled_iso,
      };
      if (draftId) {
        // For updates, allow clearing scheduled_for by sending ""
        const updPayload = { ...payload, scheduled_for: scheduled_iso || "" };
        const { data } = await axios.patch(`${API}/admin/ai-command/drafts/${draftId}`, updPayload, { headers: getAuthHeaders() });
        setDrafts(prev => prev.map(d => d.id === draftId ? data : d));
        toast.success(scheduled_iso ? "Scheduled send updated" : "Draft updated");
      } else {
        const { data } = await axios.post(`${API}/admin/ai-command/drafts`, payload, { headers: getAuthHeaders() });
        setDrafts(prev => [data, ...prev]);
        setDraftId(data.id);
        toast.success(scheduled_iso
          ? `Scheduled for ${new Date(scheduled_iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}`
          : "Draft saved");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save draft");
    }
    setSaving(false);
  };

  const loadDraft = (d) => {
    setDraftId(d.id);
    setSelectedIds(d.recipient_ids || []);
    setIntent(d.intent || "");
    setTone(d.tone || "professional");
    setSubject(d.subject || "");
    setBody(d.body || "");
    setContext("");
    setSentSummary(null);
    // Convert ISO to "YYYY-MM-DDTHH:mm" for the input[type=datetime-local]
    if (d.scheduled_for) {
      const dt = new Date(d.scheduled_for);
      const pad = (n) => String(n).padStart(2, "0");
      const local = `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
      setScheduledFor(local);
    } else {
      setScheduledFor("");
    }
    toast.success("Draft loaded");
  };

  const deleteDraft = async (id) => {
    if (!window.confirm("Delete this draft?")) return;
    try {
      await axios.delete(`${API}/admin/ai-command/drafts/${id}`, { headers: getAuthHeaders() });
      setDrafts(prev => prev.filter(d => d.id !== id));
      if (draftId === id) setDraftId(null);
      toast.success("Draft deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const sendNow = async () => {
    if (selectedIds.length === 0) return toast.error("Pick at least one recipient");
    if (!subject.trim() || !body.trim()) return toast.error("Subject and body required");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/ai-command/send`,
        { recipient_ids: selectedIds, subject, body },
        { headers: getAuthHeaders() }
      );
      setSentSummary(data);
      toast.success(`Sent to ${data.sent?.length || 0} of ${data.total} recipient${data.total === 1 ? "" : "s"}`);
      // If a draft existed, delete it — it's now sent
      if (draftId) {
        await axios.delete(`${API}/admin/ai-command/drafts/${draftId}`, { headers: getAuthHeaders() }).catch(() => {});
        setDrafts(prev => prev.filter(d => d.id !== draftId));
        setDraftId(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || "Send failed");
    }
    setSending(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-black uppercase tracking-tight flex items-center gap-2">
          <Sparkles size={18} className="text-red-500" /> AI Command Centre
        </h2>
        <p className="text-xs text-zinc-500">Draft and send emails to your team with AI. Save drafts to send later.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
        {/* LEFT: composer */}
        <div className="space-y-6">
          {/* Recipient picker */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
              <Users size={11} /> To ({selectedIds.length} selected)
            </label>
            {staff.length === 0 ? (
              <p className="text-xs text-zinc-600">No staff found. Invite team members first.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {staff.map(s => {
                  const active = selectedIds.includes(s.id);
                  const isSelf = s.id === (currentUser?.id || currentUser?._id);
                  return (
                    <button
                      key={s.id}
                      onClick={() => toggleRecipient(s.id)}
                      data-testid={`ai-recipient-${s.id}`}
                      className={`tap inline-flex items-center gap-2 text-xs font-bold border-2 px-3 py-2 transition-colors ${active ? "border-red-600 bg-red-600/10 text-white" : "border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"}`}
                    >
                      {active && <Check size={12} className="text-red-500" />}
                      <span className="truncate max-w-[160px]">{s.name}</span>
                      {isSelf && <span className="text-[9px] text-zinc-500">(you)</span>}
                      <span className="text-[9px] uppercase tracking-widest text-zinc-500">{s.role === "admin" ? "adm" : "mem"}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Intent + tone */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_200px] gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">
                What do you want to convey?
              </label>
              <textarea
                rows={3}
                value={intent}
                onChange={e => setIntent(e.target.value)}
                placeholder={"e.g.\n• Ask everyone to finalize the landing page mockup by Friday\n• Thank the team for crushing last week's delivery\n• Announce a new client just signed — send a kickoff call invite for Monday"}
                data-testid="ai-intent"
                className="w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none resize-none"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">Tone</label>
              <select
                value={tone}
                onChange={e => setTone(e.target.value)}
                data-testid="ai-tone"
                className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none"
              >
                {TONES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 mt-4 mb-1.5 block">
                Extra context <span className="normal-case text-zinc-700">(optional)</span>
              </label>
              <input
                value={context}
                onChange={e => setContext(e.target.value)}
                placeholder="e.g. client name, deadline date..."
                className="w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none"
              />
            </div>
          </div>

          <button
            onClick={generate}
            disabled={generating || !intent.trim() || selectedIds.length === 0}
            data-testid="ai-generate"
            className="btn-brutal btn-shine w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-red-600 text-white font-bold uppercase tracking-widest py-3 px-6 border-2 border-white hover:bg-white hover:text-black text-xs disabled:opacity-50"
          >
            {generating ? <><Loader2 size={14} className="animate-spin" /> Drafting...</>
              : <><Wand2 size={14} /> Draft with AI</>}
          </button>

          {/* Draft editor */}
          {(subject || body) && (
            <div className="border border-red-600/30 bg-red-600/5 p-4 sm:p-5 space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-[10px] uppercase tracking-widest text-red-500 flex items-center gap-1.5">
                  <FileText size={11} /> Draft — edit before sending
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={generate} disabled={generating} title="Regenerate"
                    data-testid="ai-regenerate"
                    className="tap touch-target inline-flex items-center justify-center text-zinc-400 hover:text-white disabled:opacity-50">
                    <RefreshCw size={14} className={generating ? "animate-spin" : ""} />
                  </button>
                  <button onClick={resetAll} title="Clear everything"
                    className="tap touch-target inline-flex items-center justify-center text-zinc-400 hover:text-red-500">
                    <X size={14} />
                  </button>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">Subject</label>
                <input value={subject} onChange={e => setSubject(e.target.value)}
                  data-testid="ai-subject"
                  className="w-full bg-transparent border border-white/10 px-4 py-2.5 text-sm text-white focus:border-red-600 outline-none" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">Message Body</label>
                <textarea rows={10} value={body} onChange={e => setBody(e.target.value)}
                  data-testid="ai-body"
                  className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none resize-y leading-relaxed font-mono" />
              </div>

              {/* Schedule send */}
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 flex items-center gap-1.5">
                  <Clock size={11} /> Schedule Send
                  <span className="text-zinc-700 normal-case">(leave empty to send manually)</span>
                </label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="datetime-local"
                    value={scheduledFor}
                    onChange={e => setScheduledFor(e.target.value)}
                    data-testid="ai-schedule-at"
                    min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                    className="flex-1 bg-[#0A0A0A] border border-white/10 px-4 py-2.5 text-sm text-white focus:border-red-600 outline-none"
                  />
                  {scheduledFor && (
                    <button onClick={() => setScheduledFor("")}
                      data-testid="ai-clear-schedule"
                      className="tap inline-flex items-center justify-center gap-1 text-[10px] uppercase tracking-widest text-zinc-400 hover:text-red-500 border border-white/10 hover:border-red-600/40 px-3 py-2">
                      <X size={11} /> Clear
                    </button>
                  )}
                </div>
                {scheduledFor && (
                  <p className="text-[11px] text-emerald-400 mt-2 flex items-center gap-1">
                    <Calendar size={11} /> Auto-sends on {new Date(scheduledFor).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                  </p>
                )}
              </div>

              {sentSummary && (
                <div className="border border-emerald-600/30 bg-emerald-600/5 p-3 text-xs">
                  <p className="text-emerald-400 font-bold uppercase tracking-widest text-[10px] mb-1">✓ Sent</p>
                  {sentSummary.sent?.map(s => (
                    <p key={s.email} className="text-zinc-400"><Check size={11} className="inline mr-1 text-emerald-500" /> {s.email}</p>
                  ))}
                  {sentSummary.failed?.length > 0 && (
                    <>
                      <p className="text-red-400 font-bold uppercase tracking-widest text-[10px] mt-2 mb-1">× Failed</p>
                      {sentSummary.failed.map(f => (
                        <p key={f.email} className="text-zinc-400"><AlertCircle size={11} className="inline mr-1 text-red-500" /> {f.email}: {f.error}</p>
                      ))}
                    </>
                  )}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-white/5">
                <button onClick={saveDraft} disabled={saving} data-testid="ai-save-draft"
                  className="tap inline-flex items-center justify-center gap-2 bg-white text-black py-3 px-5 text-xs font-bold uppercase tracking-widest hover:bg-zinc-200 transition-colors disabled:opacity-50">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : scheduledFor ? <Clock size={14} /> : <Save size={14} />}
                  {scheduledFor
                    ? (draftId ? "Update Schedule" : "Schedule Send")
                    : (draftId ? "Update Draft" : "Save as Draft")}
                </button>
                {!scheduledFor && (
                  <button onClick={sendNow} disabled={sending || selectedIds.length === 0}
                    data-testid="ai-send-now"
                    className="btn-brutal btn-shine flex-1 inline-flex items-center justify-center gap-2 bg-red-600 text-white py-3 px-5 text-xs font-bold uppercase tracking-widest border-2 border-white hover:bg-white hover:text-black transition-colors disabled:opacity-50">
                    {sending ? <><Loader2 size={14} className="animate-spin" /> Sending...</>
                      : <><Send size={14} /> Send Now to {selectedIds.length} recipient{selectedIds.length === 1 ? "" : "s"}</>}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: drafts sidebar */}
        <aside className="border border-white/5 bg-[#111] p-4 h-fit lg:sticky lg:top-24">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3 flex items-center gap-1.5">
            <FileText size={11} /> Saved Drafts ({drafts.length})
          </p>
          {drafts.length === 0 ? (
            <p className="text-xs text-zinc-600 italic">No saved drafts yet. Draft an email and hit "Save as Draft" to keep it here.</p>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
              {drafts.map(d => {
                const isScheduled = d.status === "scheduled" && d.scheduled_for;
                return (
                  <div key={d.id}
                    className={`group border p-3 transition-colors ${draftId === d.id ? "border-red-600/50 bg-red-600/5" : isScheduled ? "border-emerald-600/30 bg-emerald-600/5 hover:border-emerald-600/60" : "border-white/5 hover:border-white/20"}`}
                  >
                    <button onClick={() => loadDraft(d)} data-testid={`ai-draft-${d.id}`}
                      className="text-left w-full">
                      {isScheduled && (
                        <p className="text-[9px] uppercase tracking-widest text-emerald-400 mb-1 flex items-center gap-1">
                          <Clock size={9} /> Scheduled · {new Date(d.scheduled_for).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}
                        </p>
                      )}
                      <p className="text-xs font-bold text-white truncate mb-1">{d.subject || "(no subject)"}</p>
                      <p className="text-[10px] text-zinc-500 truncate">To: {(d.recipient_names || []).join(", ") || "—"}</p>
                      {!isScheduled && (
                        <p className="text-[10px] text-zinc-600 mt-1">{new Date(d.updated_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}</p>
                      )}
                    </button>
                    <button onClick={() => deleteDraft(d.id)}
                      data-testid={`ai-draft-delete-${d.id}`}
                      className="tap mt-2 text-[10px] uppercase tracking-widest text-zinc-600 hover:text-red-500 inline-flex items-center gap-1">
                      <Trash2 size={10} /> {isScheduled ? "Cancel send" : "Delete"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
